import { AppData, Metadata, Registry } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { getScalingMultiplier } from 'support/generalUtils';
import { secondsToMilliseconds } from './timeUtils';

export interface getAdTagOptionalParams {
  w?: number;
  h?: number;
  csid?: string | null;
  adZone?: string | null;
  isLive?: boolean;
  shouldEncodeParams?: boolean;
}

export interface getMappingParams {
  adZone?: string | null;
  w?: number;
  h?: number;
  isLive?: boolean;
  csid?: string | null;
  guid: string;
}

export const getAdTagParams = async (
  params: Record<string, string> | undefined,
  guid: string,
  optionalParams: getAdTagOptionalParams,
): Promise<Record<string, string>> => {
  const {
    w,
    h,
    csid,
    adZone,
    isLive = false,
    shouldEncodeParams = false,
  } = optionalParams;
  if (!AppData || !params) return {};

  const mappingParams: getMappingParams = { adZone, isLive, w, h, csid, guid };

  const {
    adZoneParam,
    appId,
    appVersion,
    cacheBuster,
    cacheBusterCsid,
    cacheBusterStream,
    cacheBusterString,
    deviceAdId,
    gpp,
    gppSid,
    height,
    idtype,
    ip,
    isLat,
    npa,
    osVersion,
    uoo,
    userAgent,
    usPrivacy,
    width,
    nexstarId,
  } = await getAdParamMapping(shouldEncodeParams, mappingParams);
  const adTagParams: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    adTagParams[key] = value
      .replace(/{{AD_ZONE}}/g, adZoneParam)
      .replace(/{{appid}}/g, appId)
      .replace(/{{appVersion}}/g, appVersion) // can be both appVersion and appversion
      .replace(/{{appversion}}/g, appVersion)
      .replace(/{{app.APP_VERSION}}/g, appVersion)
      .replace(/{{app.idtype}}/g, idtype)
      .replace(/{{app.is_lat}}/g, isLat)
      .replace(/{{app.OS_VERSION}}/g, osVersion)
      .replace(/{{app.npa}}/g, npa)
      .replace(/{{app.rdid}}/g, deviceAdId)
      .replace(/{{app.us_privacy}}/g, usPrivacy)
      .replace(/{{gpp_string}}/g, gpp)
      .replace(/{{gpp_sid}}/g, gppSid)
      .replace(/{{CACHEBUSTER}}/g, cacheBuster) // can be both CACHEBUSTER and cachebuster have slightly different values
      .replace(/{{Cachebuster}}/g, cacheBusterString)
      .replace(/{{CACHEBUSTER_CSID}}/g, cacheBusterCsid)
      .replace(/{{CACHEBUSTER_STREAM}}/g, cacheBusterStream)
      .replace(/{{CW_GUID}}/g, guid)
      .replace(/{{DEVICE_ID}}/g, deviceAdId)
      .replace(/{{d_id}}/g, deviceAdId)
      .replace(/{{HEIGHT}}/g, height)
      .replace(/{{IP}}/g, ip)
      .replace(/{{osver}}/g, osVersion)
      .replace(/{{NEXSTAR_ID}}/g, nexstarId)
      .replace(/{{NONCE}}/g, '')
      .replace(/{{USERAGENT}}/g, userAgent)
      .replace(/{{UserOptOut}}/g, uoo) // we've seen this passed as both UserOptOut and userOptOut
      .replace(/{{userOptOut}}/g, uoo)
      .replace(/{{WIDTH}}/g, width);
  });

  return adTagParams;
};

const getAdParamMapping = async (
  encodeValues: boolean,
  paramsObj: getMappingParams,
) => {
  const deviceInfo = await AppData!.device.getDeviceInfo();
  await AppData!.blueconicService?.init();
  const { adZone = '', csid = '', guid, isLive = false, w, h } = paramsObj;

  const appId = AppData!.device.getAppId() ?? '';
  const appVersion = Metadata.appVersion() ?? '';
  const cacheBuster = `${new Date().getTime()}`;
  const cacheBusterString = Math.floor(Date.now() / 1000).toString();
  const cacheBusterCsid = csid ?? guid;
  const cacheBusterStream = Date.now().toString().slice(-6);
  const deviceAdId = deviceInfo.adId;
  const height = `${h || 1080 / getScalingMultiplier()}`;
  const { idtype } = AppData!.google;
  const ip = deviceInfo.ip;
  const npa = `${AppData!.privacyService.getRdpTag()}`;
  const osVersion = deviceInfo.osVersion;
  const uoo = AppData!.privacyService.getDoNotSell();
  const usPrivacy = AppData!.privacyService.getIabUsPrivacy();
  const { gpp, gppSid } = AppData!.privacyService.getIabGpp();
  const userAgent = window.navigator.userAgent;
  const width = `${w || 1920 / getScalingMultiplier()}`;
  // isLat is a different value for the live stream url vs vod/brightcove ad tag params
  const isLat = isLive
    ? uoo
      ? '1'
      : '0'
    : `${AppData!.privacyService.getRdpTag()}`;
  const nexstarId = uoo
    ? deviceAdId
    : AppData!.blueconicService?.getId() || deviceAdId;

  const adParams = {
    adZoneParam: adZone ?? '',
    appId,
    appVersion,
    cacheBuster,
    cacheBusterCsid,
    cacheBusterStream,
    cacheBusterString,
    deviceAdId,
    gpp,
    gppSid,
    height,
    idtype,
    ip,
    isLat,
    npa,
    osVersion,
    uoo: `${uoo}`,
    userAgent,
    usPrivacy,
    width,
    nexstarId,
  };

  const result: { [key: string]: any } = {};

  Object.entries(adParams).forEach(([key, value]) => {
    result[key] = encodeValues ? encodeURIComponent(value) : value;
  });

  return result;
};

export const buildParamsStringFromObj = (paramsObj: Record<string, string>) => {
  return Object.entries(paramsObj)
    .map(([key, value]) => {
      return `${key}=${value}`;
    })
    .join('&');
};

export const getCommonLanguageCode = (lang: string): string => {
  switch (lang.toLocaleLowerCase()) {
    case 'en':
    case 'eng':
    case 'en-ca':
    case 'english':
      return 'english';
    default:
      return lang;
  }
};

/**
 * sets a timeout with an added jitter, used for preventing time based events from thousands of requests at once (like live events)
 *
 * @param fn timeout function
 * @param delay delay added to the jitter
 */
export const setJitterTimeout = (fn: () => void, delay?: number): number => {
  const jitter =
    Math.random() * secondsToMilliseconds(constants.timers.jitterDelay);

  return Registry.setTimeout(fn, (delay ?? 0) + jitter);
};

export const isCCEnabled = () => {
  return (
    AppData?.storageService.subtitle.get() !== constants.closedCaption.none ||
    !!AppData?.device.getCcSettings().enabled
  );
};
